// ─────────────────────────────────────────
// Login.js — login + forgot password
// ─────────────────────────────────────────

import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export default function Login() {
  const [mode, setMode] = useState('login') // 'login' | 'forgot'

  // Login state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginError, setLoginError] = useState(null)
  const [loginLoading, setLoginLoading] = useState(false)

  // Forgot password state
  const [resetEmail, setResetEmail] = useState('')
  const [resetError, setResetError] = useState(null)
  const [resetSent, setResetSent] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)

  async function handleLogin(e) {
    e.preventDefault()
    setLoginLoading(true)
    setLoginError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { setLoginError(error.message); setLoginLoading(false) }
  }

  async function handleForgot(e) {
    e.preventDefault()
    if (!resetEmail.trim()) return
    setResetLoading(true)
    setResetError(null)

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
      redirectTo: `${window.location.origin}/reset-password`
    })

    if (error) {
      setResetError(error.message)
    } else {
      setResetSent(true)
    }
    setResetLoading(false)
  }

  // ── FORGOT PASSWORD VIEW ──
  if (mode === 'forgot') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.logo}>BREAKIE</div>
          <div style={s.tagline}>Reset your password</div>

          {resetSent ? (
            <div style={s.successBox}>
              <div style={s.successIcon}>📬</div>
              <div style={s.successTitle}>Check your inbox</div>
              <div style={s.successBody}>
                We sent a reset link to <strong style={{ color: 'white' }}>{resetEmail}</strong>.
                Click the link in that email to set a new password.
              </div>
              <button style={s.secondaryBtn} onClick={() => { setMode('login'); setResetSent(false); setResetEmail('') }}>
                ← Back to Log In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot} style={s.form}>
              <div style={s.fieldHint}>
                Enter your email and we'll send you a link to reset your password.
              </div>
              <input
                style={s.input}
                type="email"
                placeholder="Your email address"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                required
                autoFocus
              />

              {resetError && <div style={s.error}>{resetError}</div>}

              <button style={s.btn} type="submit" disabled={resetLoading}>
                {resetLoading ? 'Sending...' : 'Send Reset Link →'}
              </button>

              <button type="button" style={s.ghostBtn} onClick={() => setMode('login')}>
                ← Back to Log In
              </button>
            </form>
          )}
        </div>
      </div>
    )
  }

  // ── LOGIN VIEW ──
  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>BREAKIE</div>
        <div style={s.tagline}>Your Golf Life, Social.</div>

        <form onSubmit={handleLogin} style={s.form}>
          <input
            style={s.input}
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />

          <div style={{ position: 'relative' }}>
            <input
              style={s.input}
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          <div style={s.forgotWrap}>
            <button type="button" style={s.forgotLink} onClick={() => { setMode('forgot'); setResetEmail(email) }}>
              Forgot password?
            </button>
          </div>

          {loginError && <div style={s.error}>{loginError}</div>}

          <button style={s.btn} type="submit" disabled={loginLoading}>
            {loginLoading ? 'Logging in...' : 'Log In →'}
          </button>
        </form>

        <div style={s.footer}>
          Don't have an account?{' '}
          <Link to="/signup" style={s.link}>Sign up free</Link>
        </div>
      </div>
    </div>
  )
}

const s = {
  page: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24, fontFamily: "'Barlow', sans-serif"
  },
  card: {
    background: '#1e3325', borderRadius: 24, padding: '48px 36px',
    width: '100%', maxWidth: 400, textAlign: 'center'
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 42, letterSpacing: 4, color: '#c8f135', marginBottom: 8
  },
  tagline: { fontSize: 14, color: '#6aad80', marginBottom: 36 },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  input: {
    width: '100%', background: '#0e1f13', border: '1.5px solid #2a4a2e',
    borderRadius: 12, padding: '14px 16px', fontSize: 15, color: 'white',
    outline: 'none', fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  },
  forgotWrap: { textAlign: 'right', marginTop: -6 },
  forgotLink: {
    background: 'none', border: 'none', color: '#6aad80',
    fontSize: 12, cursor: 'pointer', fontFamily: "'Barlow', sans-serif",
    padding: 0, textDecoration: 'underline'
  },
  error: {
    background: 'rgba(232,64,64,0.15)', border: '1px solid rgba(232,64,64,0.3)',
    color: '#f87171', borderRadius: 10, padding: '10px 14px', fontSize: 13
  },
  btn: {
    background: '#c8f135', color: '#0e1f13', border: 'none', borderRadius: 12,
    padding: '16px', fontSize: 16, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    cursor: 'pointer', marginTop: 4
  },
  ghostBtn: {
    background: 'none', border: '1.5px solid #2a4a2e', borderRadius: 12,
    padding: '13px', fontSize: 14, fontWeight: 700, color: '#6aad80',
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  fieldHint: {
    fontSize: 13, color: '#6aad80', lineHeight: 1.5, textAlign: 'left', marginBottom: 4
  },
  successBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12
  },
  successIcon: { fontSize: 40, marginBottom: 4 },
  successTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#c8f135', letterSpacing: 0.5
  },
  successBody: {
    fontSize: 13, color: '#6aad80', lineHeight: 1.6, textAlign: 'center'
  },
  secondaryBtn: {
    marginTop: 8, background: 'none', border: '1.5px solid #2a4a2e',
    borderRadius: 12, padding: '13px 24px', fontSize: 14, fontWeight: 700,
    color: '#6aad80', cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  footer: { marginTop: 28, fontSize: 13, color: '#6aad80' },
  link: { color: '#c8f135', fontWeight: 700, textDecoration: 'none' }
}
